import { cn } from '../../../lib/utils.ts'
import { Badge } from '../badge/badge.tsx'
import { Card } from '../card/card.tsx'
import { Heading } from '../heading/heading.tsx'
import { Text } from '../text/text.tsx'

/**
 * ProductCard is a presentational slot compound for an insurance product card:
 * header (media + title + price) + a body of attribute rows + actions. No data, no
 * domain — every piece of content is consumer-supplied. Compose only the slots you need;
 * omitting a slot omits that part. Modeled on `Page` (a plain `<div>` compound, no context).
 *
 * @summary Composable product card (header / media / title / price / rows / actions).
 */
function ProductCardRoot(props: ProductCardRoot.Props) {
  const { className, ...rest } = props
  return <Card.Root data-slot="product-card-root" className={cn('flex flex-col gap-60', className)} {...rest} />
}
namespace ProductCardRoot {
  export type Props = React.ComponentProps<'div'>
}

function ProductCardHeader(props: ProductCardHeader.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="product-card-header"
      className={cn(
        `
          flex items-center gap-50
          max-md:flex-col max-md:items-start
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace ProductCardHeader {
  export type Props = React.ComponentProps<'div'>
}

function ProductCardMedia(props: ProductCardMedia.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="product-card-media"
      className={cn(
        `
          flex size-[48px] shrink-0 items-center justify-center
          [&_svg:not([class*='size-'])]:size-6
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace ProductCardMedia {
  export type Props = React.ComponentProps<'div'>
}

function ProductCardTitle(props: ProductCardTitle.Props) {
  const { className, ...rest } = props
  return (
    <Heading
      render={<p />}
      data-slot="product-card-title"
      variant="subtitle"
      className={cn('min-w-0 flex-1', className)}
      {...rest}
    />
  )
}
namespace ProductCardTitle {
  export type Props = Omit<Heading.Props, 'variant' | 'weight' | 'render'>
}

function ProductCardPrice(props: ProductCardPrice.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="product-card-price"
      className={cn(
        `
          ml-auto flex shrink-0 items-center gap-inline
          max-md:ml-0
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace ProductCardPrice {
  export type Props = React.ComponentProps<'div'>
}

function ProductCardPriceAmount(props: ProductCardPriceAmount.Props) {
  const { className, ...rest } = props
  return (
    <Text
      data-slot="product-card-price-amount"
      variant="body1"
      weight="semibold"
      className={cn('whitespace-nowrap text-content-base-main', className)}
      {...rest}
    />
  )
}
namespace ProductCardPriceAmount {
  export type Props = Omit<Text.Props, 'variant' | 'weight'>
}

function ProductCardPriceRecurrence(props: ProductCardPriceRecurrence.Props) {
  // `data-slot` (passed here) overrides Badge's own 'badge' slot via Badge's trailing prop spread.
  return <Badge data-slot="product-card-price-recurrence" intent="base" {...props} />
}
namespace ProductCardPriceRecurrence {
  export type Props = Badge.Props
}

function ProductCardContent(props: ProductCardContent.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="product-card-content"
      // Figma splits the card into title + this block, 24px apart, and spaces the rows from the
      // actions by 16px inside it — tighter than the card's own `gap-60` rhythm.
      className={cn('flex w-full flex-col gap-50', className)}
      {...rest}
    />
  )
}
namespace ProductCardContent {
  export type Props = React.ComponentProps<'div'>
}

function ProductCardBody(props: ProductCardBody.Props) {
  const { className, ...rest } = props
  return <div data-slot="product-card-body" className={cn('flex w-full flex-col', className)} {...rest} />
}
namespace ProductCardBody {
  export type Props = React.ComponentProps<'div'>
}

function ProductCardRow(props: ProductCardRow.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="product-card-row"
      // Grid rather than flex: mobile stacks the value under the label while the trailing icon
      // spans both rows and stays vertically centered. Figma pads rows more tightly on desktop.
      className={cn(
        `
          grid grid-cols-[1fr_auto_auto] items-center gap-x-50 py-20
          max-md:grid-cols-[1fr_auto] max-md:gap-y-20 max-md:py-30
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace ProductCardRow {
  export type Props = React.ComponentProps<'div'>
}

function ProductCardRowLabel(props: ProductCardRowLabel.Props) {
  const { className, ...rest } = props
  return (
    <span
      data-slot="product-card-row-label"
      className={cn('col-start-1 row-start-1 min-w-0 text-20 leading-30 font-400 text-content-base-basic', className)}
      {...rest}
    />
  )
}
namespace ProductCardRowLabel {
  export type Props = React.ComponentProps<'span'>
}

function ProductCardRowValue(props: ProductCardRowValue.Props) {
  const { className, intent = 'base', ...rest } = props
  return (
    <span
      data-slot="product-card-row-value"
      className={cn(
        `
          col-start-2 row-start-1 text-right text-20 leading-30 font-600
          max-md:col-start-1 max-md:row-start-2 max-md:text-left
        `,
        intent === 'link' ? 'text-content-base-link' : 'text-content-base-main',
        className,
      )}
      {...rest}
    />
  )
}
namespace ProductCardRowValue {
  export type Props = React.ComponentProps<'span'> & {
    /** Value color. `'link'` → the link token (purple). @default 'base' */
    intent?: 'base' | 'link'
  }
}

function ProductCardRowTrailing(props: ProductCardRowTrailing.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="product-card-row-trailing"
      className={cn(
        `
          col-start-3 row-start-1 flex w-[16px] shrink-0 items-center justify-center self-stretch
          text-content-base-basic
          max-md:col-start-2 max-md:row-span-2
          [&_svg:not([class*='size-'])]:size-4
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace ProductCardRowTrailing {
  export type Props = React.ComponentProps<'div'>
}

function ProductCardActions(props: ProductCardActions.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="product-card-actions"
      className={cn(
        'flex items-center justify-end gap-inline',
        'max-md:w-full max-md:flex-col-reverse max-md:items-stretch',
        className,
      )}
      {...rest}
    />
  )
}
namespace ProductCardActions {
  export type Props = React.ComponentProps<'div'>
}

const ProductCard = {
  Root: ProductCardRoot,
  Header: ProductCardHeader,
  Media: ProductCardMedia,
  Title: ProductCardTitle,
  Price: ProductCardPrice,
  PriceAmount: ProductCardPriceAmount,
  PriceRecurrence: ProductCardPriceRecurrence,
  Content: ProductCardContent,
  Body: ProductCardBody,
  Row: ProductCardRow,
  RowLabel: ProductCardRowLabel,
  RowValue: ProductCardRowValue,
  RowTrailing: ProductCardRowTrailing,
  Actions: ProductCardActions,
}

namespace ProductCard {
  export namespace Root {
    export type Props = ProductCardRoot.Props
  }
  export namespace Header {
    export type Props = ProductCardHeader.Props
  }
  export namespace Media {
    export type Props = ProductCardMedia.Props
  }
  export namespace Title {
    export type Props = ProductCardTitle.Props
  }
  export namespace Price {
    export type Props = ProductCardPrice.Props
  }
  export namespace PriceAmount {
    export type Props = ProductCardPriceAmount.Props
  }
  export namespace PriceRecurrence {
    export type Props = ProductCardPriceRecurrence.Props
  }
  export namespace Content {
    export type Props = ProductCardContent.Props
  }
  export namespace Body {
    export type Props = ProductCardBody.Props
  }
  export namespace Row {
    export type Props = ProductCardRow.Props
  }
  export namespace RowLabel {
    export type Props = ProductCardRowLabel.Props
  }
  export namespace RowValue {
    export type Props = ProductCardRowValue.Props
  }
  export namespace RowTrailing {
    export type Props = ProductCardRowTrailing.Props
  }
  export namespace Actions {
    export type Props = ProductCardActions.Props
  }
}

export { ProductCard }
